/**
 * Formatter / parser helpers for antd `InputNumber`.
 *
 * Use with the component typed as `InputNumber<NumericInputValue>`: with
 * literal `min`/`max` props TypeScript otherwise infers the value type as
 * `0 | 100`, and the parser has to be allowed to return a string.
 */

/** Value an `InputNumber` holds while being edited — a number, or the raw display string. */
export type NumericInputValue = string | number;

const isEmpty = (value?: NumericInputValue): boolean => value === '' || value == null;

/**
 * Strips display decoration (`%`, currency symbols, thousands separators,
 * spaces) so only the number is left.
 *
 * Returns `''` for an empty field rather than `Number('')`, which is `0`: antd
 * reads `''` as "cleared" and stores `null`, so a required rule still fires.
 * Parsing to `0` instead silently saved a zero whenever someone cleared a field.
 *
 * @param display - The text currently in the input.
 * @returns The bare numeric string, or `''` when nothing numeric was entered.
 */
export const parseNumericInput = (display?: string): NumericInputValue =>
  (display ?? '').replace(/[^\d.-]/g, '');

/**
 * Groups thousands with commas while leaving the decimals exactly as typed:
 * 6000 shows as 6,000 and 1234567.5 as 1,234,567.5. Only the whole-number part
 * is grouped, so 0.12345 is never split into 0.12,345.
 *
 * Pair with `parseNumericInput`, which strips the commas back out, so the form
 * still holds a plain number.
 *
 * @param value - The input's current value.
 */
export const formatThousandsInput = (value?: NumericInputValue): string => {
  if (isEmpty(value)) return '';
  const [whole, ...decimals] = `${value}`.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimals.length ? `${grouped}.${decimals.join('')}` : grouped;
};

/**
 * Shows a value with a trailing `%`, and nothing when the field is empty.
 *
 * @param value - The input's current value.
 */
export const formatPercentInput = (value?: NumericInputValue): string => (isEmpty(value) ? '' : `${value}%`);

/**
 * Builds a formatter that prefixes a currency symbol, and shows nothing when
 * the field is empty.
 *
 * @param symbol - Prefix to show, e.g. `£` or `$`.
 */
export const currencyInputFormatter =
  (symbol: string) =>
  (value?: NumericInputValue): string =>
    isEmpty(value) ? '' : `${symbol}${formatThousandsInput(value)}`;

/**
 * Formatter and parser for a money or count field: thousands grouped as it is
 * typed, a plain number underneath. Spread onto an `InputNumber`.
 */
export const thousandsInputProps = {
  formatter: formatThousandsInput,
  parser: parseNumericInput
};
